import { DateTime } from 'luxon'

import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc'
import { StoppageDto } from '../DTOs/Stoppage/StoppageDto'
import { queryClient } from '../queryClient'

type CachedPage = { data?: unknown[] }

// Note: stoppage types the maintenance list excludes (see StoppagesRepository).
const NON_MAINTENANCE_STOPPAGE_TYPE_IDS = new Set([1, 3, 4])

export interface OperationStatusPatch {
  status: string
  statusDescription: string
  stoppageColourHub?: string
  stoppageId?: number
  currentStoppageTypeId?: number
  currentStoppageSubTypeId?: number
  timeIn?: DateTime
}

// Note: a machine's status is calculated server-side from its open stoppages, so adding one offline
// left the Operations list showing UP - which in turn hid the nested table's Time Out picker, since
// that only renders for a machine that is down. Patch the cached operation row the way closing a
// stoppage offline already patches it back to UP (see setAsAvailable in OperationsTable).
// Note: the nested stoppage table's cache key includes the machine's status, so changing the status
// offline points the table at a key with nothing in it - the expanded row then claimed to be
// unavailable offline. Copy the machine's cached stoppages to the key for its new status.
export function mirrorPlantStoppagesForStatus(
  plantId: number,
  dateIso: string,
  shift: string,
  status: string | undefined,
): void {
  if (!plantId || !status) return

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'operations', 'plantStoppages', dateIso, shift],
  })) {
    const parts = key as unknown[]
    if (parts[5] !== plantId || parts[6] === status || !Array.isArray(cached?.data)) continue

    const target = [...parts]
    target[6] = status
    queryClient.setQueryData(target, cached)
  }
}

// Note: the Operations list's expanded row reads its stoppages from this per-plant cache entry, so a
// stoppage added offline has to be written into it. Appending to the visible table alone (the
// DataTable's own row source) put the row on screen but lost it the moment the page remounted, and
// the Create Multiple Stoppages batch has no visible table to append to at all - its rows were
// missing from the Operations list entirely. Every status variant of the key gets the row: the key
// includes the machine's status, which this stoppage is about to change.
export function appendCachedPlantStoppage(dto: StoppageDto, dateIso: string, shift: string, plantId: number): void {
  if (!plantId) return

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'operations', 'plantStoppages', dateIso, shift],
  })) {
    const rows = cached?.data
    if ((key as unknown[])[5] !== plantId || !Array.isArray(rows)) continue
    if (dto.stoppageId && rows.some(row => isSameStoppage(row, dto.stoppageId!))) continue

    queryClient.setQueryData(key, {
      ...cached,
      data: [...rows, dto],
      totalCount: ((cached as { totalCount?: number })?.totalCount ?? rows.length) + 1,
    })
  }
}

// Note: closing a stoppage offline should hand the machine back, exactly as setting it available
// does - the server recomputes the status on a refresh, but offline nothing does. Only once no open
// stoppage is left for that machine, since a machine can be down for more than one reason.
// Note: closedStoppageId is the stoppage just given a time out. It is excluded from the scan below,
// because whether the cache already shows it closed depends on every page holding it having been
// patched first - and when one had not been, the machine stayed down with its own closed stoppage
// counted as the reason. Excluding it by id makes the outcome independent of that ordering.
export function patchCachedOperationAvailability(
  plantId: number,
  dateIso: string,
  shift: string,
  closedStoppageId?: number,
): void {
  if (!plantId) return

  // Note: scoped to this shift. Scanning every cached shift meant a machine carried into the new
  // shift still down kept the previous shift's open stoppage in view, so it never came back up.
  for (const [, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'operations', 'plantStoppages', dateIso, shift],
  })) {
    const rows = cached?.data
    if (!Array.isArray(rows)) continue
    for (const row of rows as StoppageDto[]) {
      if (row?.plantId !== plantId || row.endTime != null) continue
      if (closedStoppageId && row.stoppageId === closedStoppageId) continue
      return
    }
  }

  patchCachedOperationStatus(plantId, dateIso, shift, {
    status: 'UP',
    statusDescription: '',
    stoppageColourHub: '',
    stoppageId: undefined,
    currentStoppageTypeId: undefined,
    currentStoppageSubTypeId: undefined,
    timeIn: undefined,
  })
}

// Note: scoped to one shift. A machine is carried into a shift created offline under the same plant
// id, so scanning every cached shift meant taking a machine down in the new shift also took it down
// in the completed one, rewriting a shift the crew had already signed off.
export function patchCachedOperationStatus(
  plantId: number,
  dateIso: string,
  shift: string,
  patch: OperationStatusPatch,
): void {
  if (!plantId) return

  mirrorPlantStoppagesForStatus(plantId, dateIso, shift, patch.status)

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'operations', 'stoppageCalcs', dateIso, shift],
  })) {
    const rows = cached?.data
    if (!Array.isArray(rows) || !rows.some(row => (row as StoppageCalc)?.plantId === plantId)) continue

    queryClient.setQueryData(key, {
      ...cached,
      data: rows.map(row => (row as StoppageCalc)?.plantId === plantId
        ? new StoppageCalc({ ...(row as StoppageCalc), ...patch })
        : row),
    })
  }
}

// Note: a stoppage added offline is appended to the table it was added in, but the Stoppages page
// keeps its own cached list for the shift, so the new record was missing there until the queue
// synced. Append it to that list too (its cache entry exists for a shift created offline because the
// provisional shift seeds an empty one).
export function appendCachedStoppage(dto: StoppageDto, dateIso: string, shift: string): void {
  // Note: the Stoppages page reads /stoppages/maintenance, which excludes these stoppage types
  // server-side. Appending regardless put rows in that list the server would never return - the crew
  // saw a shift's worth of pre-start delays in the maintenance list. Keep in step with
  // StoppagesRepository.GetPagedStoppagesAsync's maintenanceOnly filter.
  if (NON_MAINTENANCE_STOPPAGE_TYPE_IDS.has(dto.stoppageTypeId)) return

  const withNavObjects = new StoppageDto({ ...dto, plant: dto.plant ?? cachedPlantFor(dto.plantId) })

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'stoppages', 'list', dateIso, shift],
  })) {
    const rows = cached?.data
    if (!Array.isArray(rows)) continue

    // Note: never list the same stoppage twice. A stoppage carried into a shift created offline can
    // reach this list from two directions - copied from the completed shift's cache, and rebuilt from
    // the machine's row in the Operations list - and the list showed both, the second with an empty
    // Machine cell because a rebuilt row has no plant. Keep whichever copy knows its plant.
    const existing = withNavObjects.stoppageId
      ? rows.findIndex(row => isSameStoppage(row, withNavObjects.stoppageId!))
      : -1

    if (existing >= 0) {
      const known = rows[existing] as StoppageDto
      if (known.plant || !withNavObjects.plant) continue

      queryClient.setQueryData(key, {
        ...cached,
        data: rows.map((row, index) => (index === existing ? withNavObjects : row)),
      })
      continue
    }

    queryClient.setQueryData(key, {
      ...cached,
      data: [withNavObjects, ...rows],
      totalCount: ((cached as { totalCount?: number })?.totalCount ?? rows.length) + 1,
    })
  }
}

// Note: the Stoppages list renders Machine and Department from the nested plant object, which an
// optimistic row does not have - the rows appeared blank in those columns. Lift it off any other
// cached stoppage for the same plant, which carries the plant and its department.
export function cachedPlantFor(plantId: number | undefined): StoppageDto['plant'] {
  if (!plantId) return undefined

  // Note: it has to be a plant that knows its department, because that is what colours the Machine
  // cell. Accepting any plant meant this found the minimal one a queued row carries - id and name, no
  // department - and returned it as the answer, so the name rendered and the colour never did. The
  // row's own copy remains the caller's fallback, which is why a name still shows when nothing here
  // knows the department.
  for (const [, cached] of queryClient.getQueriesData<CachedPage>({ queryKey: ['dataTable'] })) {
    const rows = cached?.data
    if (!Array.isArray(rows)) continue

    for (const row of rows as StoppageDto[]) {
      if (row?.plantId === plantId && row.plant?.department && 'startTime' in row) return row.plant
    }
  }
  return plantFromAnyCache(plantId)
}

// Note: on a shift with no cached stoppages there is nothing to lift a plant off, and every new row
// rendered with an empty Machine column. Two fallbacks, best first: any cached plant list (the option
// lists the pages load carry the department, and the Machine cell colours itself from the department's
// hexColour), then the Operations list, which has one row per machine but no department - a name with
// no colour, which still beats a blank cell. Scanning by shape rather than by key so it keeps working
// wherever those lists are cached.
function plantFromAnyCache(plantId: number): StoppageDto['plant'] {
  for (const [, cached] of queryClient.getQueriesData({ queryKey: [] })) {
    const rows = Array.isArray(cached) ? cached : (cached as CachedPage)?.data
    if (!Array.isArray(rows)) continue

    for (const row of rows) {
      const candidate = row as { id?: number; plantId?: number; plantRef?: string; department?: unknown }
      if (!candidate?.plantRef || !candidate.department) continue
      if (candidate.id === plantId || candidate.plantId === plantId) return candidate as StoppageDto['plant']
    }
  }

  for (const [, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'operations', 'stoppageCalcs'],
  })) {
    for (const row of (cached?.data ?? []) as StoppageCalc[]) {
      if (row?.plantId === plantId && row.machineName) {
        return { id: plantId, plantRef: row.machineName } as StoppageDto['plant']
      }
    }
  }
  return undefined
}

// Note: 'startTime' distinguishes a real stoppage row from a StoppageCalc, which is the Operations
// list's one-row-per-machine shape. StoppageCalc also carries stoppageId and locationId, but its
// locationId is the machine's location, so patching those rows with a stoppage's fields would move
// the machine. Matching on the class is no good either - rows restored from the persisted cache are
// plain objects.
function isSameStoppage(row: unknown, stoppageId: number): boolean {
  if (!row || typeof row !== 'object' || !('startTime' in row)) return false
  return (row as StoppageDto).stoppageId === stoppageId
}

// Note: the same stoppage is cached under more than one table's key - the Stoppages page's own list
// and the Operations list's per-plant nested table. An offline edit only patches the table it was
// made in, so the other screen kept showing the old values until the queue synced and both keys
// were invalidated. Patch every cached page that holds the row instead.
export function patchCachedStoppages(updated: StoppageDto): StoppageDto | undefined {
  const stoppageId = updated.stoppageId
  if (!stoppageId) return undefined

  // Note: with useDefineForClassFields, a StoppageDto's unset fields are own properties equal to
  // undefined. Spreading them over the cached row would blank good values (including the nested
  // plant/type/location objects the tables render), so only merge the fields that are set.
  // Note: an absent nav object is null as often as undefined - the row a table hands back carries the
  // edited scalars and nothing else - and letting that null through blanked the Downtime Type as soon
  // as a time out was entered. A null endTime, by contrast, is a real edit (a stoppage reopened), so
  // only the nav objects are protected.
  const navProps = new Set(['plant', 'stoppageType', 'stoppageSubType', 'location'])
  const changed = Object.fromEntries(
    Object.entries(updated).filter(([field, value]) =>
      value !== undefined && !(value === null && navProps.has(field)))
  )

  let merged: StoppageDto | undefined

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({ queryKey: ['dataTable'] })) {
    const rows = cached?.data
    if (!Array.isArray(rows) || !rows.some(row => isSameStoppage(row, stoppageId))) continue

    queryClient.setQueryData(key, {
      ...cached,
      data: rows.map(row => {
        if (!isSameStoppage(row, stoppageId)) return row
        merged = new StoppageDto({ ...(row as StoppageDto), ...changed })
        return merged
      }),
    })
  }

  return merged
}
